import React, { Suspense } from "react";
import type { RouteObject } from "react-router-dom";
import { i18nTranslate, i18nStart, i18nSetLang } from "@didi/i18n-plugin";
import { inApp, isUnoNew, SOFI_BRAND_NAME, defaultLanguage } from "@/common/js/constant";
import BasicLayout from "@/components/Layout/BasicLayout";
import RouteError from "@/components/RouteError";
import DownloadIcon from "@/assets/images/cycleSelection/download.png";
import i18nIndex from "@/lang/i18n-index";
import i18nInfo from "@/lang/i18n-info";
import { customerServiceLoader } from "@/views/CustomerService/loader";
import { safePerceptionLoader } from "@/views/SafePerception/loader";
import { accountStatusLoader } from "@/views/AccountStatus/loader";
import { limitDetailsLoader } from "@/views/LimitDetails/loader";
import { freezeDetailsLoader } from "@/views/FreezeDetails/loader";
import { transactionDetailsLoader } from "@/views/TransactionDetails/loader";
import { billDetailsLoader } from "@/views/BillDetails/loader";
import { eventDetailsLoader } from "@/views/EventDetails/loader";
import { interestRulesLoader } from "@/views/Interest/rule/loader";
import { safetyInstructionLoader } from "@/views/SafetyInstruction/loader";
import { statementLoader } from "@/views/Statement/loader";
import { waitingListLoader } from "@/views/WaitingList/loader";
import { transactionLoader } from "@/views/Transaction/loader";
import { speiLoader, speiExperimentLoader } from "@/views/Transfer/loader";
import { couponsListLoader } from "@/views/CouponsList/loader";
import { returnsSimulationLoader } from "@/views/ReturnsSimulation/loader";
import { simulatedEarningsLoader } from "@/views/EarningsSimulation/loader";
import { limitGuideLoader } from "@/views/TransferPaymentLimits/guide/loader";
import { limitSelectLoader } from "@/views/TransferPaymentLimits/select/loader";
import { limitSetLoader } from "@/views/TransferPaymentLimits/set/loader";
import { annualTaxLoader } from "@/views/AnnualTax/loader";
import { cycleSelectionLoader } from "@/views/CycleSelection/loader";
import { taxUploadLoader } from "@/views/TaxInfo/upload/loader";
import { taxFaqLoader } from "@/views/TaxInfo/faq/loader";
import { mgmHistoryLoader } from "@/views/MGM/History/loader";
import { mgmLandingLoader } from "@/views/MGM/M2/loader";
import { mgmRuleLoader } from "@/views/MGM/Rule/loader";

// ========== i18n 初始化（必须在路由配置之前） =========== //
i18nStart(i18nIndex, i18nInfo);
i18nSetLang(defaultLanguage);

// ========== 分包 =========== //
// uno=1 命中新版（逻辑/UI 分离），缺省/其他走旧版；未命中的 UI 不会被打包下载
// 首页
const SofiHome = React.lazy(() => import("@/views/Home"));
// 客服
const CustomerService = React.lazy(() => import("@/views/CustomerService"));
const CustomerServiceDetails = React.lazy(() => import("@/views/CustomerServiceDetails"));
// 账户状态
const AccountStatus = React.lazy(() => import("@/views/AccountStatus"));
// 冻结详情
const FreezeDetails = React.lazy(() => import("@/views/FreezeDetails"));
// AB实验版本
const SpeiExperimentPage = React.lazy(() =>
  isUnoNew ? import("@/views/Transfer/page/entry") : import("@/views/Transfer/SpeiExperiment"),
);
const VideoPlayerPage = React.lazy(() => import("@/views/Transfer/VideoPlayer"));
// 交易结果
const TransactionDetails = React.lazy(() =>
  isUnoNew ? import("@/views/TransactionDetails/page/entry") : import("@/views/TransactionDetails"),
);
// 账单详情
const BillDetails = React.lazy(() =>
  isUnoNew ? import("@/views/BillDetails/page/entry") : import("@/views/BillDetails"),
);
// 限制限额详情
const LimitDetails = React.lazy(() => import("@/views/LimitDetails"));
// 交易记录
const Transaction = React.lazy(() =>
  isUnoNew ? import("@/views/Transaction/page/entry") : import("@/views/Transaction"),
);
// 安全感知落地页
const SafePerception = React.lazy(() => import("@/views/SafePerception"));
// 个人信息页面
const PageProfile = React.lazy(() =>
  isUnoNew ? import("@/views/Personal/page/entry") : import("@/views/Personal/profile"),
);
// 统计周期选择页面（uno=1 命中新版逻辑/UI 分离版，缺省/其他走旧版）
const CycleSelection = React.lazy(() =>
  isUnoNew ? import("@/views/CycleSelection/page/entry") : import("@/views/CycleSelection"),
);
// 预览pdf（uno=1 命中新版逻辑/UI 分离版，缺省/其他走旧版）
const PreviewPdf = React.lazy(() =>
  isUnoNew ? import("@/views/PreviewPdf/page/entry") : import("@/views/PreviewPdf"),
);

// 入金
const SpeiPage = React.lazy(() => import("@/views/Transfer/spei"));

// 受益人首页/列表页
const Successor = React.lazy(() =>
  isUnoNew ? import("@/views/Successor/page/entry") : import("@/views/Successor"),
);
// 受益人表单页
const SuccessorAdd = React.lazy(() =>
  isUnoNew ? import("@/views/SuccessorAdd/page/entry") : import("@/views/SuccessorAdd"),
);
// 已开sofipo户 不可didi充值 引导页
const SofipoOpenedTip = React.lazy(() => import("@/views/SofipoOpenedTip"));
// 已开sofipo户 不可didi充值 引导页
const PageWaitingList = React.lazy(() => import("@/views/WaitingList"));
// 已开sofipo户 不可didi充值 引导页
const PageWaitingListSuccess = React.lazy(() => import("@/views/WaitingList/PageSuccess"));
// 消息中心退订页
const Unsubscribe = React.lazy(() => import("@/views/Unsubscribe"));
// 销户流程
const AccountCloseSurvey = React.lazy(() => import("@/views/AccountClose/SurveyPage"));
const AccountCloseVerify = React.lazy(() => import("@/views/AccountClose/VerifyPage"));
const AccountCloseResult = React.lazy(() => import("@/views/AccountClose/ResultPage"));
// 限时加息活动详情页
const EventDetails = React.lazy(() => import("@/views/EventDetails"));
// 新版限时加息活动详情页
const NewEventDetail = React.lazy(() => import("@/views/NewEventDetail"));
// 老客限时加息活动详情页
const YieldOfferDetail = React.lazy(() => import("@/views/yield-offer/ActivityDetailPage"));
// 老客限时加息活动说明/T&C/历史活动页
const YieldOfferInfo = React.lazy(() => import("@/views/yield-offer/ActivityInfoPage"));
// 老客限时加息历史详情列表页
const YieldOfferHistory = React.lazy(() => import("@/views/yield-offer/HistoryDetailPage"));
const YieldOfferActivityRules = React.lazy(() => import("@/views/yield-offer/ActivityRulesPage"));
// MGM
const MGMInvite = React.lazy(() => import("@/views/MGM/M1/index"));
const MGMLanding = React.lazy(() => import("@/views/MGM/M2/index"));
// MGM Rule Page
const MGMRule = React.lazy(() => import("@/views/MGM/Rule/index"));
// MGM Ercode Page
const MGMErcode = React.lazy(() => import("@/views/MGM/Ercode/index"));
// MGM History Page
const MGMHistory = React.lazy(() => import("@/views/MGM/History/index"));
// 活动规则详情页
const ActivityRuleDetails = React.lazy(() => import("@/views/ActivityRuleDetails"));
// 券列表页
const CouponsList = React.lazy(() => import("@/views/CouponsList"));
// 利率规则页面
const InterestRules = React.lazy(() =>
  isUnoNew ? import("@/views/Interest/rule/page/entry") : import("@/views/Interest/rule"),
);
// 收益模拟页面
const ReturnsSimulation = React.lazy(() => import("@/views/ReturnsSimulation"));
// 新版收益模拟页面
const SimulatedEarnings = React.lazy(() =>
  isUnoNew ? import("@/views/EarningsSimulation/page/entry") : import("@/views/EarningsSimulation"),
);
// 账户说明页面
const SafetyInstruction = React.lazy(() =>
  isUnoNew ? import("@/views/SafetyInstruction/page/entry") : import("@/views/SafetyInstruction"),
);

// 安全背书页（入金链路归因 node 8）
const Security = React.lazy(() => import("@/views/Security"));
// 税务信息上传页面
const TaxUpload = React.lazy(() => import("@/views/TaxInfo/upload"));
// 账单类型选择页面
const Statement = React.lazy(() =>
  isUnoNew ? import("@/views/Statement/page/entry") : import("@/views/Statement"),
);
// 预扣税账单选择页面
const AnnualTax = React.lazy(() =>
  isUnoNew ? import("@/views/AnnualTax/page/entry") : import("@/views/AnnualTax"),
);

const TaxUploading = React.lazy(() => import("@/views/TaxInfo/loading"));

const TaxInfo = React.lazy(() => import("@/views/TaxInfo/info"));

const TaxFaq = React.lazy(() => import("@/views/TaxInfo/faq"));

const TaxError = React.lazy(() => import("@/views/TaxInfo/error"));

// 限额修改引导页（uno=1 命中新版逻辑/UI 分离版，缺省/其他走旧版）
const LimitGuide = React.lazy(() =>
  isUnoNew
    ? import("@/views/TransferPaymentLimits/guide/page/entry")
    : import("@/views/TransferPaymentLimits/guide"),
);

// 限额修改选择页（uno=1 命中新版逻辑/UI 分离版，缺省/其他走旧版）
const LimitSelect = React.lazy(() =>
  isUnoNew
    ? import("@/views/TransferPaymentLimits/select/page/entry")
    : import("@/views/TransferPaymentLimits/select"),
);

// 限额修改设置页（uno=1 命中新版逻辑/UI 分离版，缺省/其他走旧版）
const LimitSet = React.lazy(() =>
  isUnoNew
    ? import("@/views/TransferPaymentLimits/set/page/entry")
    : import("@/views/TransferPaymentLimits/set"),
);

// 限额修改结果页（uno=1 命中新版逻辑/UI 分离版，缺省/其他走旧版）
const LimitResult = React.lazy(() =>
  isUnoNew
    ? import("@/views/TransferPaymentLimits/result/page/entry")
    : import("@/views/TransferPaymentLimits/result"),
);

// KYC 电话实名问卷页面
const KycPhonePage = React.lazy(() => import("@/views/KycPhone"));

const routeConfig: RouteObject[] = [
  {
    path: "/",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<SofiHome />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/spei",
    loader: speiLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<SpeiPage />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // AB实验版本
  {
    path: "/spei-experiment",
    loader: speiExperimentLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<SpeiExperimentPage />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/video-player",
    element: (
      <Suspense fallback={<div></div>}>
        <VideoPlayerPage />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/limit",
    loader: limitDetailsLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<LimitDetails />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/billDetails",
    loader: billDetailsLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout
          hideHeader={inApp}
          title={i18nTranslate("Fintech_Payment_Optimization_BIAB_lwdr")}
          bgColor="white"
          content={<BillDetails />}
        />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/details",
    loader: transactionDetailsLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout
          hideHeader={inApp}
          title={i18nTranslate("Fintech_Payment_Optimization_bymF_byCA")}
          bgColor="white"
          content={<TransactionDetails />}
        />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/transaction",
    ...(isUnoNew ? {} : { loader: transactionLoader }),
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout
          hideHeader={inApp}
          iconUrl={DownloadIcon}
          linkUrl={"/cycleSelection"}
          title={i18nTranslate("Fintech_Payment_AF_fXUE_ZcYk")}
          bgColor="white"
          content={<Transaction />}
        />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/freezeDetails",
    loader: freezeDetailsLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <FreezeDetails />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/customerService",
    loader: customerServiceLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout
          hideHeader={inApp}
          title={i18nTranslate("Fintech_Payment_helpcenter_kDlf_knuA", {
            brand_name: SOFI_BRAND_NAME,
          })}
          content={<CustomerService />}
        />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/customerServiceDetails",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout
          hideHeader={inApp}
          title={i18nTranslate("Fintech_Payment_helpcenter_jjmJ_itJL")}
          content={<CustomerServiceDetails />}
        />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/accountStatus",
    loader: accountStatusLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout title="" content={<AccountStatus />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/safePerception",
    loader: safePerceptionLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <SafePerception />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 个人信息页面
  {
    path: "/personal/profile",
    element: (
      <Suspense fallback={<div></div>}>
        <PageProfile />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/cycleSelection",
    loader: cycleSelectionLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout
          hideHeader={inApp || isUnoNew}
          title={i18nTranslate("Fintech_Payment_Bankstatement_JpFH_XKLj")}
          content={<CycleSelection />}
        />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/previewPdf",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout
          hideHeader={inApp || isUnoNew}
          title={i18nTranslate("Fintech_Payment_Bankstatement_cwrx_hynB")}
          content={<PreviewPdf />}
        />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/successor",
    element: (
      <Suspense fallback={<div></div>}>
        <Successor />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 新增受益人
  {
    path: "/successor/add",
    element: (
      <Suspense fallback={<div></div>}>
        <SuccessorAdd />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 编辑受益人
  {
    path: "/successor/add/:beneficiaryNo",
    element: (
      <Suspense fallback={<div></div>}>
        <SuccessorAdd />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 已开sofipo户情况下 点击didi充值 跳转的引导页
  {
    path: "/sofipoOpenedTip",
    element: (
      <Suspense fallback={<div></div>}>
        <SofipoOpenedTip />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 已开sofipo户情况下 点击didi充值 跳转的引导页
  {
    path: "/waiting-list",
    loader: waitingListLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <PageWaitingList />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 已开sofipo户情况下 点击didi充值 跳转的引导页
  {
    path: "/waiting-list-success",
    element: (
      <Suspense fallback={<div></div>}>
        <PageWaitingListSuccess />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 消息中心退订页面
  {
    path: "/messageUnsubscribe",
    element: (
      <Suspense fallback={<div></div>}>
        <Unsubscribe />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 销户流程-问卷填写页面
  {
    path: "/account-close/survey",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<AccountCloseSurvey />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 销户流程-预校验页面
  {
    path: "/account-close/verify",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<AccountCloseVerify />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 销户流程-销户结果页面
  {
    path: "/account-close/result",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<AccountCloseResult />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 限时加息活动详情页面
  {
    path: "/eventDetails",
    loader: eventDetailsLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <EventDetails />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 新版限时加息活动详情页面
  {
    path: "/new/eventDetails",
    element: (
      <Suspense fallback={<div></div>}>
        <NewEventDetail />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 老客限时加息活动详情页
  {
    path: "/yield-offer/activity",
    element: (
      <Suspense fallback={<div></div>}>
        <YieldOfferDetail />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 老客限时加息活动说明/T&C/历史活动页
  {
    path: "/yield-offer/info",
    element: (
      <Suspense fallback={<div></div>}>
        <YieldOfferInfo />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 老客限时加息历史详情列表页
  {
    path: "/yield-offer/history",
    element: (
      <Suspense fallback={<div></div>}>
        <YieldOfferHistory />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 老客限时加息活动规则页
  {
    path: "/yield-offer/activity-rules",
    element: (
      <Suspense fallback={<div></div>}>
        <YieldOfferActivityRules />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // MGM Invite Page
  {
    path: "/mgm/invite",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<MGMInvite />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // MGM Landing Page
  {
    path: "/mgm/landing",
    loader: mgmLandingLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<MGMLanding />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // MGM Rule Page
  {
    path: "/mgm/rules",
    loader: mgmRuleLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<MGMRule />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // MGM Ercode Page
  {
    path: "/mgm/ercode",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<MGMErcode />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // MGM History Page
  {
    path: "/mgm/history",
    loader: mgmHistoryLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<MGMHistory />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 活动规则详情页
  {
    path: "/activityRuleDetails",
    element: (
      <Suspense fallback={<div></div>}>
        <ActivityRuleDetails />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 券列表页
  {
    path: "/coupons",
    loader: couponsListLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <CouponsList />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 利率规则页面
  {
    path: "/interest-rules",
    loader: interestRulesLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<InterestRules />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 收益模拟页面
  {
    path: "/returns-simulation",
    loader: returnsSimulationLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<ReturnsSimulation />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 新版收益模拟页面
  {
    path: "/simulated-earnings",
    loader: simulatedEarningsLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<SimulatedEarnings />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 账户说明页面
  {
    path: "/safety-instruction",
    loader: safetyInstructionLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<SafetyInstruction />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 安全背书页（入金链路归因 node 8）
  {
    path: "/security",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<Security />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/tax/upload",
    loader: taxUploadLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<TaxUpload />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/tax/loading",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<TaxUploading />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/tax/info",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<TaxInfo />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/tax/faq",
    loader: taxFaqLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<TaxFaq />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "/tax/error",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<TaxError />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 账单类型选择页面
  {
    path: "/statement",
    loader: statementLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={inApp || isUnoNew} content={<Statement />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 预扣税账单选择页面
  {
    path: "/statement/annual-tax",
    loader: annualTaxLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={inApp || isUnoNew} content={<AnnualTax />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 限额修改引导页
  {
    path: "/limit/guide",
    loader: limitGuideLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<LimitGuide />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 限额修改选择页
  {
    path: "/limit/select",
    loader: limitSelectLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<LimitSelect />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 限额修改设置页
  {
    path: "/limit/set",
    loader: limitSetLoader,
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<LimitSet />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // 限额修改结果页
  {
    path: "/limit/result",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<LimitResult />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  // KYC 电话实名问卷页面
  {
    path: "/kyc/phone",
    element: (
      <Suspense fallback={<div></div>}>
        <BasicLayout hideHeader={true} content={<KycPhonePage />} />
      </Suspense>
    ),
    errorElement: <RouteError />,
  },
  {
    path: "*",
    element: <div></div>,
  },
];

export default routeConfig;
